import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Download, FileSignature, MessageCircle, PenLine, Plus, Settings2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/KpiCard";
import { download } from "@/components/phones/PhoneRegister";
import { FormDialog } from "@/components/forms/FormDialog";
import { SignNowDialog } from "@/components/forms/SignNowDialog";
import { TemplatesDialog } from "@/components/forms/TemplatesDialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const linkOf = (f) => `${window.location.origin}/modulo/${f.token}`;
const copy = async (f) => {
  try { await navigator.clipboard.writeText(linkOf(f)); toast.success("Link copiato: invialo al driver"); } catch { window.prompt("Copia il link", linkOf(f)); }
};
const whatsapp = (f) => window.open(`https://wa.me/?text=${encodeURIComponent(`Ciao ${f.driver_name}, firma il modulo "${f.template_name}": ${linkOf(f)}`)}`, "_blank");

export default function FormsPage() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [stations, setStations] = useState([]);
  const [stationId, setStationId] = useState("");
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const [signing, setSigning] = useState(null);
  const [tpl, setTpl] = useState(false);
  useEffect(() => { api.get("/stations").then((r) => { setStations(r.data); if (isAdmin && r.data[0]) setStationId((s) => s || r.data[0].id); }); }, [isAdmin]);
  const sid = isAdmin ? stationId : user.station_id;
  const load = useCallback(() => { if (sid) api.get("/forms", { params: { station_id: isAdmin ? sid : undefined } }).then((r) => setRows(r.data)); }, [sid, isAdmin]);
  useEffect(() => { load(); }, [load]);
  const remove = async (f) => {
    if (!window.confirm(`Eliminare il modulo di ${f.driver_name}?`)) return;
    try { await api.delete(`/forms/${f.id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const ro = user.supervisor;

  return (
    <div className="fade-up">
      <PageHeader title="Moduli" subtitle="Moduli con testo prestabilito, foto del documento e firma del driver: subito in app, con un link personale o scaricato in PDF.">
        {isAdmin && !ro && <Button variant="outline" onClick={() => setTpl(true)} data-testid="forms-templates-button"><Settings2 className="mr-2 h-4 w-4" /> Modelli</Button>}
        {!ro && <Button onClick={() => setCreating(true)} disabled={!sid} data-testid="forms-new-button"><Plus className="mr-2 h-4 w-4" /> Nuovo modulo</Button>}
      </PageHeader>
      {isAdmin && (
        <div className="mb-5 flex gap-2 overflow-x-auto pb-1" data-testid="forms-station-tabs">
          {stations.map((st) => (
            <button key={st.id} onClick={() => setStationId(st.id)} data-testid={`forms-tab-${st.id}`}
              className={`shrink-0 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${stationId === st.id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>{st.name}</button>
          ))}
        </div>
      )}
      <div className="space-y-2" data-testid="forms-list">
        {rows.map((f) => (
          <div key={f.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-4" data-testid={`form-row-${f.id}`}>
            <FileSignature className={`h-5 w-5 shrink-0 ${f.signed ? "text-emerald-600" : "text-amber-500"}`} />
            <div className="min-w-0 flex-1">
              <p className="font-semibold">{f.driver_name} <span className="font-normal text-slate-500">· {f.template_name}</span></p>
              <p className="text-xs text-slate-500">Creato il {new Date(f.created_at).toLocaleDateString("it-IT")}{f.created_by_name && ` da ${f.created_by_name}`}
                {f.signed && ` · firmato il ${new Date(f.signed_at).toLocaleDateString("it-IT")} ${f.signed_via === "link" ? "dal link" : "in app"}`}
                {` · foto ${f.photos.filter((p) => p.has).length}/${f.photos.length}`}</p>
            </div>
            <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${f.signed ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`} data-testid={`form-status-${f.id}`}>{f.signed ? "Firmato" : "Da firmare"}</span>
            <div className="flex flex-wrap gap-1">
              {!f.signed && !ro && <Button size="sm" onClick={() => setSigning(f)} data-testid={`form-sign-${f.id}`}><PenLine className="mr-1 h-4 w-4" /> Firma ora</Button>}
              {!f.signed && <Button size="sm" variant="outline" onClick={() => copy(f)} data-testid={`form-copy-${f.id}`}><Copy className="mr-1 h-4 w-4" /> Link</Button>}
              {!f.signed && <Button size="sm" variant="outline" onClick={() => whatsapp(f)} data-testid={`form-whatsapp-${f.id}`}><MessageCircle className="mr-1 h-4 w-4" /> WhatsApp</Button>}
              <Button size="sm" variant="outline" onClick={() => download(`/forms/${f.id}/pdf`, {}, `${f.template_name}_${f.driver_name}.pdf`)} data-testid={`form-pdf-${f.id}`}><Download className="mr-1 h-4 w-4" /> PDF</Button>
              {!ro && (isAdmin || !f.signed) && <Button size="sm" variant="ghost" className="text-slate-400" onClick={() => remove(f)} data-testid={`form-delete-${f.id}`}><Trash2 className="h-4 w-4" /></Button>}
            </div>
          </div>
        ))}
        {!rows.length && <p className="rounded-xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500" data-testid="forms-empty">Nessun modulo.</p>}
      </div>
      {creating && <FormDialog sid={sid} isAdmin={isAdmin} onClose={() => setCreating(false)} onSaved={(f) => { setCreating(false); load(); if (f) setSigning(f); }} />}
      {signing && <SignNowDialog form={signing} onClose={() => setSigning(null)} onSigned={() => { setSigning(null); load(); }} />}
      {tpl && <TemplatesDialog onClose={() => setTpl(false)} />}
    </div>
  );
}
